<template>
  <vue-date-picker
    v-model="value"
    ref="datepickerRef"
    :model-type="DatePickerFormats.API_FORMAT"
    :formats="{ input: formatDisplayDate }"
    :placeholder="context.attrs.placeholder"
    :floating="{ strategy: 'fixed', arrow: false }"
    :input-attrs="{ name: context.node.name, clearable: !!context.clearable }"
    :hide-navigation="['month', 'year']"
    :config="{ monthChangeOnScroll: false }"
    :time-config="{ enableTimePicker: false }"
    :action-row="{ showPreview: false, selectBtnLabel: 'Apply' }"
    :ui="{ menu: [context.multiCalendars ? 'dp__menu_multi-calendars' : ''] }"
    :range="context.range ? { partialRange: false } : false"
    :multi-calendars="context.multiCalendars"
    :disabled="context.disabled"
    @closed="handleClosed"
  >
    <template
      v-if="context.clearable"
      #clear-icon="{ clear }"
    >
      <button
        type="button"
        class="inline-flex items-center gap-1 text-sm font-semibold cursor-pointer"
        @click="clear"
      >
        <app-icon
          size="sm"
          name="x-circle"
          class="text-gray-900"
        />
        <span class="text-gray-600">Clear {{ context.range ? 'dates' : 'date' }}</span>
      </button>
    </template>
  </vue-date-picker>
</template>

<script setup>
import dayjs from 'dayjs';
import { computed, ref } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { ApiDateFormats, DatePickerFormats, DatePickerDisplayFormats } from '@/constants/dates.js';
import '@vuepic/vue-datepicker/dist/main.css';

const props = defineProps({
  context: Object,
});

const datepickerRef = ref(null);

// vue-datepicker passes a bare Date (rather than the range array) when it's
// building the emitted model value for one side of a range, not the display text.
const formatEmittedRangeValue = (date) => (date ? dayjs(date).format(ApiDateFormats.DATE) : '');

const formatDisplayDate = (date) => {
  const isEmittedValue = !Array.isArray(date);
  if (isEmittedValue) {
    return formatEmittedRangeValue(date);
  }

  const [start, end] = date;
  if (!start) return '';
  if (!end) return dayjs(start).format(DatePickerDisplayFormats.DATE);

  const startDate = dayjs(start);
  const endDate = dayjs(end);

  if (startDate.isSame(endDate, 'day')) {
    return startDate.format(DatePickerDisplayFormats.DATE_VERBOSE);
  }

  if (startDate.isSame(endDate, 'month')) {
    return `${startDate.format('MMMM D')}-${endDate.format('D')}, ${endDate.format('YYYY')}`;
  }

  return `${startDate.format(DatePickerDisplayFormats.DATE)} - ${endDate.format(DatePickerDisplayFormats.DATE)}`;
};

const value = computed({
  get: () => props.context._value || null,
  set: (newValue) => {
    props.context.node.input(newValue);
  },
});

const handleClosed = () => {
  let date = dayjs();

  if (Array.isArray(value.value) && value.value.length > 0) {
    date = dayjs(value.value[1]);
  }

  datepickerRef.value?.setMonthYear({ month: date.month(), year: date.year() });
};
</script>

<style>
@reference '@/assets/main.css';

:root {
  /* General */
  --dp-font-family: 'Inter', sans-serif;
  --dp-border-radius: calc(var(--spacing) * 2);
  --dp-cell-border-radius: 50%;

  /* Sizing */
  --dp-month-year-row-button-size: calc(var(--spacing) * 8);
  --dp-cell-size: calc(var(--spacing) * 10);
  --dp-common-padding: calc(var(--spacing) * 3);
  --dp-menu-min-width: 320px;
  --dp-row-margin: var(--spacing) 0;
  --dp-menu-padding: calc(var(--spacing) * 5) calc(var(--spacing) * 6);

  /* Font sizes */
  --dp-font-size: inherit;

  /* Transitions */
  --dp-animation-duration: 0s;
}

.dp__theme_light {
  --dp-text-color: var(--color-base-content);
  --dp-primary-color: var(--color-brand-600);
  --dp-border-color: var(--color-gray-200);
  --dp-icon-color: var(--color-gray-500);
}

.dp__input {
  @apply border-0 p-0 pl-7 placeholder:text-gray-500;
}

.dp__input_icons {
  @apply w-5 h-5 p-0;
}

.dp__menu {
  --dp-border-radius: calc(var(--spacing) * 3);
}

.dp__menu_inner {
  @apply gap-3;
}

.dp__overlay {
  @apply rounded-xl;
}

.dp__overlay_container {
  @apply px-4 py-2;
}

.dp__calendar_header_item {
  @apply inline-flex items-center justify-center font-medium;
}

.dp__today {
  @apply border-0 bg-gray-100;
}

.dp__active_date,
.dp__range_start,
.dp__range_end {
  @apply border-0 bg-brand-600 text-white;
}

.dp__action_row {
  @apply p-4 border-t border-t-gray-200;
}

.dp__action_buttons {
  @apply flex-1 justify-center gap-3;
}

.dp__menu_multi-calendars {
  & .dp__action_buttons {
    @apply flex-0;
  }
}

.dp__action_button {
  @apply flex-1 m-0 btn;
}

.dp__action_cancel {
  @apply btn-outline border-gray-200 hover:bg-gray-50 hover:border-gray-300;
}

.dp__action_select {
  @apply btn-primary;
}

.dp__calendar_header_separator {
  @apply hidden;
}
</style>
